import React, { useEffect, useState, useCallback, useRef } from "react";
import API from "../../../services/api";

const Beneficiary = () => {
  const [items, setItems] = useState([]);
  const [villList, setVillList] = useState([]);
  const [deptList, setDeptList] = useState([]);
  const [schemeList, setSchemeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voterLoading, setVoterLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [search, setSearch] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Village autocomplete
  const [villSearch, setVillSearch] = useState("");
  const [villDropdown, setVillDropdown] = useState([]);
  const [showVillDrop, setShowVillDrop] = useState(false);
  const [selectedVill, setSelectedVill] = useState(null);
  const villRef = useRef(null);

  // Voter search
  const [voterSearch, setVoterSearch] = useState("");
  const [voterList, setVoterList] = useState([]);
  const [showVoterDrop, setShowVoterDrop] = useState(false);
  const voterRef = useRef(null);
  const searchTimer = useRef(null);

  const years = [];
  for (let y = 2020; y <= 2030; y++) years.push(`${y}-${y + 1}`);

  const emptyForm = {
    name: "",
    phone: "",
    epic: "",
    department: "",
    scheme_name: "",
    financial_year: "",
    status: "",
    budget_type: "Regular",
    subsidy_amount: "",
    beneficiary_amount: "",
    total_amount: "",
  };
  const [form, setForm] = useState(emptyForm);

  // ── Fetch Masters ─────────────────────────────────────────
  const fetchMasters = useCallback(async () => {
    try {
      const [vill, dept, scheme] = await Promise.all([
        API.get("/masters/villages"),
        API.get("/masters/departments"),
        API.get("/masters/projectcategories"),
      ]);
      setVillList(vill.data.data);
      setDeptList(dept.data.data);
      setSchemeList(scheme.data.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  // ── Fetch Beneficiaries ───────────────────────────────────
  const fetchItems = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await API.get("/beneficiary-projects/", {
          params: {
            page,
            per_page: 10,
            search: search || undefined,
            financial_year: filterYear || undefined,
            status: filterStatus || undefined,
          },
        });
        setItems(res.data.data.beneficiaries);
        setTotalPages(res.data.data.pagination.total_pages);
        setTotalRecords(res.data.data.pagination.total);
        setCurrentPage(page);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [search, filterYear, filterStatus],
  );

  useEffect(() => {
    fetchMasters();
    fetchItems();
  }, []);

  // ── Close dropdowns on outside click ─────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (villRef.current && !villRef.current.contains(e.target))
        setShowVillDrop(false);
      if (voterRef.current && !voterRef.current.contains(e.target))
        setShowVoterDrop(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Village Autocomplete ──────────────────────────────────
  const handleVillSearch = (val) => {
    setVillSearch(val);
    setSelectedVill(null);
    setVoterSearch("");
    setVoterList([]);
    setShowVoterDrop(false);

    if (val.length >= 1) {
      const filtered = villList.filter((v) =>
        v.name.toLowerCase().includes(val.toLowerCase()),
      );
      setVillDropdown(filtered.slice(0, 8));
      setShowVillDrop(true);
    } else {
      setVillDropdown([]);
      setShowVillDrop(false);
    }
  };

  const selectVillage = (village) => {
    setVillSearch(village.name);
    setSelectedVill(village);
    setShowVillDrop(false);
    setVillDropdown([]);
    setVoterSearch("");
    setVoterList([]);
    setShowVoterDrop(false);
  };

  // ── Voter Search with debounce ────────────────────────────
  const handleVoterSearch = (val) => {
    setVoterSearch(val);
    setShowVoterDrop(true);

    if (searchTimer.current) clearTimeout(searchTimer.current);

    if (val.length >= 2) {
      setVoterLoading(true);
      searchTimer.current = setTimeout(async () => {
        try {
          const res = await API.get("/voters/", {
            params: { page: 1, per_page: 15, search: val },
          });
          setVoterList(res.data.data.voters || []);
        } catch (err) {
          console.error(err);
          setVoterList([]);
        } finally {
          setVoterLoading(false);
        }
      }, 400);
    } else {
      setVoterList([]);
      setVoterLoading(false);
    }
  };

  // ── Select Voter → Auto-fill ──────────────────────────────
  const selectVoter = (voter) => {
    setForm((prev) => ({
      ...prev,
      name: `${voter.firstname_en || ""} ${voter.lastname_en || ""}`.trim(),
      phone: voter.mobile || "",
      epic: voter.epic_no || "",
    }));
    setVoterSearch(
      `${voter.firstname_en || ""} ${voter.lastname_en || ""}`.trim(),
    );
    setShowVoterDrop(false);
    setVoterList([]);
  };

  // ── Open Add ──────────────────────────────────────────────
  const openAdd = () => {
    setEditItem(null);
    setForm(emptyForm);
    setVillSearch("");
    setSelectedVill(null);
    setVoterSearch("");
    setVoterList([]);
    setShowVillDrop(false);
    setShowVoterDrop(false);
    setShowModal(true);
  };

  // ── Open Edit ─────────────────────────────────────────────
  const openEdit = (item) => {
    setEditItem(item);
    const vill = villList.find((v) => v.villageid === item.village_id);
    setVillSearch(vill ? vill.name : "");
    setSelectedVill(vill || null);
    setVoterSearch(item.name || "");
    setVoterList([]);
    setShowVillDrop(false);
    setShowVoterDrop(false);
    setForm({
      name: item.name || "",
      phone: item.phone || "",
      epic: item.epic || "",
      department: item.department || "",
      scheme_name: item.scheme_name || "",
      financial_year: item.financial_year || "",
      status: item.status || "",
      budget_type: item.budget_type || "Regular",
      subsidy_amount: item.subsidy_amount || "",
      beneficiary_amount: item.beneficiary_amount || "",
      total_amount: item.total_amount || "",
    });
    setShowModal(true);
  };

  // ── Save ──────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name) return alert("Please select a beneficiary from voter list");
    if (!selectedVill) return alert("Please select a village");
    if (!form.phone) return alert("Phone number is required");
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        village_id: selectedVill.villageid,
        phone: form.phone,
        epic: form.epic || null,
        department: form.department || null,
        scheme_name: form.scheme_name || null,
        financial_year: form.financial_year || null,
        status: form.status || null,
        budget_type: form.budget_type,
        subsidy_amount: form.subsidy_amount
          ? parseFloat(form.subsidy_amount)
          : 0,
        beneficiary_amount: form.beneficiary_amount
          ? parseFloat(form.beneficiary_amount)
          : 0,
        total_amount: form.total_amount ? parseFloat(form.total_amount) : 0,
      };
      if (editItem) {
        await API.put(`/beneficiary-projects/${editItem.id}`, payload);
      } else {
        await API.post("/beneficiary-projects/", payload);
      }
      setShowModal(false);
      fetchItems(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error saving");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this beneficiary?")) return;
    setDeleting(id);
    try {
      await API.delete(`/beneficiary-projects/${id}`);
      fetchItems(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-1">Beneficiary Projects</h4>
          <p className="text-muted small mb-0">
            Total <strong>{totalRecords}</strong> beneficiaries
          </p>
        </div>
        <button
          className="btn text-white fw-semibold px-4"
          style={{ background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)" }}
          onClick={openAdd}
        >
          <i className="bi bi-plus-lg me-2"></i>Add Beneficiary
        </button>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm rounded-4 mb-3">
        <div className="card-body p-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, EPIC, phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-6 col-md-2">
              <select
                className="form-select"
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
              >
                <option value="">All Years</option>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <input
                type="text"
                className="form-control"
                placeholder="Status..."
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              />
            </div>
            <div className="col-6 col-md-2">
              <button
                className="btn btn-primary w-100"
                onClick={() => fetchItems(1)}
              >
                <i className="bi bi-search me-1"></i>Search
              </button>
            </div>
            <div className="col-6 col-md-2">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearch("");
                  setFilterYear("");
                  setFilterStatus("");
                  fetchItems(1);
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-heart fs-1 d-block mb-2"></i>
              No beneficiary projects found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="px-4 py-3 small text-muted fw-semibold">
                      #
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Beneficiary
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Phone</th>
                    <th className="py-3 small text-muted fw-semibold">
                      Scheme
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Dept</th>
                    <th className="py-3 small text-muted fw-semibold">
                      Fin. Year
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Total (₹)
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Status
                    </th>
                    <th className="py-3 small text-muted fw-semibold text-end pe-4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={item.id}>
                      <td className="px-4 text-muted small">
                        {(currentPage - 1) * 10 + idx + 1}
                      </td>
                      <td>
                        <div className="fw-semibold small">{item.name}</div>
                        {item.epic && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "11px" }}
                          >
                            EPIC: {item.epic}
                          </div>
                        )}
                      </td>
                      <td className="small text-muted">{item.phone}</td>
                      <td className="small">{item.scheme_name || "—"}</td>
                      <td className="small text-muted">
                        {item.department || "—"}
                      </td>
                      <td className="small text-muted">
                        {item.financial_year || "—"}
                      </td>
                      <td className="small fw-medium">
                        {item.total_amount
                          ? `₹${Number(item.total_amount).toLocaleString("en-IN")}`
                          : "—"}
                      </td>
                      <td>
                        {item.status ? (
                          <span className="badge bg-success-subtle text-success small">
                            {item.status}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="text-end pe-4">
                        <button
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => openEdit(item)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(item.id)}
                          disabled={deleting === item.id}
                        >
                          {deleting === item.id ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <i className="bi bi-trash"></i>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer bg-white border-0 d-flex justify-content-between align-items-center py-3 px-4">
            <span className="text-muted small">
              Page {currentPage} of {totalPages}
            </span>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchItems(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                  if (page > totalPages) return null;
                  return (
                    <li
                      key={page}
                      className={`page-item ${currentPage === page ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => fetchItems(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}
                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchItems(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 rounded-4 shadow-lg">
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <h5 className="modal-title fw-bold">
                  {editItem ? "Edit Beneficiary" : "Add Beneficiary"}
                </h5>
                <button
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSave}>
                <div
                  className="modal-body px-4 py-3"
                  style={{ maxHeight: "70vh", overflowY: "auto" }}
                >
                  {/* ── STEP 1: Village Autocomplete ── */}
                  <div
                    className="p-3 rounded-3 mb-3"
                    style={{ background: "#f0f4ff" }}
                  >
                    <p
                      className="small fw-semibold mb-2"
                      style={{ color: "#1a3c5e" }}
                    >
                      <i className="bi bi-geo-alt me-1"></i>
                      Step 1 — Select Village
                    </p>

                    <div className="position-relative" ref={villRef}>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-search text-muted"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="Type village name to search..."
                          value={villSearch}
                          onChange={(e) => handleVillSearch(e.target.value)}
                          autoComplete="off"
                        />
                        {selectedVill && (
                          <span className="input-group-text bg-white">
                            <i className="bi bi-check-circle-fill text-success"></i>
                          </span>
                        )}
                      </div>

                      {/* Village Dropdown */}
                      {showVillDrop && villDropdown.length > 0 && (
                        <div
                          className="border rounded-3 shadow bg-white position-absolute w-100 mt-1"
                          style={{
                            zIndex: 9999,
                            maxHeight: "160px",
                            overflowY: "auto",
                          }}
                        >
                          {villDropdown.map((v) => (
                            <div
                              key={v.villageid}
                              className="px-3 py-2 border-bottom d-flex align-items-center gap-2"
                              style={{ cursor: "pointer" }}
                              onMouseDown={() => selectVillage(v)}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f0f4ff")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "white")
                              }
                            >
                              <i className="bi bi-geo-alt text-muted small"></i>
                              <span className="small fw-medium">{v.name}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {villSearch &&
                        villDropdown.length === 0 &&
                        showVillDrop && (
                          <div
                            className="border rounded-3 bg-white position-absolute w-100 mt-1 px-3 py-2"
                            style={{ zIndex: 9999 }}
                          >
                            <span className="text-muted small">
                              No village found
                            </span>
                          </div>
                        )}
                    </div>

                    {selectedVill && (
                      <div className="mt-2 small text-success">
                        <i className="bi bi-check-circle me-1"></i>
                        Village selected: <strong>{selectedVill.name}</strong>
                      </div>
                    )}
                  </div>

                  {/* ── STEP 2: Voter Search ── */}
                  <div
                    className="p-3 rounded-3 mb-3"
                    style={{ background: selectedVill ? "#f0f4ff" : "#f8f9fa" }}
                  >
                    <p
                      className="small fw-semibold mb-2"
                      style={{ color: selectedVill ? "#1a3c5e" : "#aaa" }}
                    >
                      <i className="bi bi-person-check me-1"></i>
                      Step 2 — Search &amp; Select Beneficiary from{" "}
                      {selectedVill ? (
                        <strong>{selectedVill.name}</strong>
                      ) : (
                        "selected village"
                      )}
                    </p>

                    <div className="position-relative" ref={voterRef}>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-search text-muted"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder={
                            selectedVill
                              ? `Search voter in ${selectedVill.name}...`
                              : "Select village first..."
                          }
                          value={voterSearch}
                          onChange={(e) => handleVoterSearch(e.target.value)}
                          disabled={!selectedVill}
                          autoComplete="off"
                        />
                        {voterLoading && (
                          <span className="input-group-text bg-white">
                            <span className="spinner-border spinner-border-sm text-primary"></span>
                          </span>
                        )}
                      </div>
                      <div className="form-text">
                        {selectedVill
                          ? "Type 2+ characters — name (English/Kannada) or EPIC no"
                          : "Select a village above to enable voter search"}
                      </div>

                      {/* Voter Dropdown */}
                      {showVoterDrop && voterList.length > 0 && (
                        <div
                          className="border rounded-3 shadow bg-white position-absolute w-100 mt-1"
                          style={{
                            zIndex: 9999,
                            maxHeight: "220px",
                            overflowY: "auto",
                          }}
                        >
                          {voterList.map((v) => (
                            <div
                              key={v.voter_id}
                              className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center"
                              style={{ cursor: "pointer" }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f0f4ff")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "white")
                              }
                            >
                              <div>
                                <div className="fw-semibold small">
                                  {v.firstname_en} {v.lastname_en}
                                </div>
                                {(v.firstname_v1 || v.lastname_v1) && (
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: "12px" }}
                                  >
                                    {v.firstname_v1} {v.lastname_v1}
                                  </div>
                                )}
                                <div
                                  className="text-muted"
                                  style={{ fontSize: "11px" }}
                                >
                                  <span className="badge bg-primary-subtle text-primary me-1">
                                    {v.epic_no}
                                  </span>
                                  {v.mobile && (
                                    <span className="me-1">📱 {v.mobile}</span>
                                  )}
                                  <span>Booth {v.booth_no}</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm ms-2 text-white flex-shrink-0"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                                }}
                                onMouseDown={() => selectVoter(v)}
                              >
                                <i className="bi bi-plus-lg me-1"></i>Select
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {showVoterDrop &&
                        voterSearch.length >= 2 &&
                        voterList.length === 0 &&
                        !voterLoading && (
                          <div
                            className="border rounded-3 bg-white position-absolute w-100 mt-1 px-3 py-2"
                            style={{ zIndex: 9999 }}
                          >
                            <span className="text-muted small">
                              <i className="bi bi-info-circle me-1"></i>
                              No voters found for "{voterSearch}"
                            </span>
                          </div>
                        )}
                    </div>

                    {/* Auto-filled voter info */}
                    {form.name && (
                      <div className="row g-2 mt-2">
                        <div className="col-12 col-md-4">
                          <label className="form-label small fw-medium mb-1">
                            Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={form.name}
                            onChange={(e) =>
                              setForm({ ...form, name: e.target.value })
                            }
                          />
                        </div>
                        <div className="col-6 col-md-4">
                          <label className="form-label small fw-medium mb-1">
                            Mobile <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={form.phone}
                            maxLength={10}
                            placeholder="10 digit mobile"
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, "");
                              setForm({ ...form, phone: val });
                            }}
                          />
                        </div>
                        <div className="col-6 col-md-4">
                          <label className="form-label small fw-medium mb-1">
                            EPIC No
                          </label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={form.epic}
                            onChange={(e) =>
                              setForm({ ...form, epic: e.target.value })
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── STEP 3: Project Details ── */}
                  <div
                    className="p-3 rounded-3"
                    style={{ background: "#f8f9fa" }}
                  >
                    <p
                      className="small fw-semibold mb-2"
                      style={{ color: "#1a3c5e" }}
                    >
                      <i className="bi bi-folder me-1"></i>
                      Step 3 — Project Details
                    </p>
                    <div className="row g-3">
                      {/* Department */}
                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-medium">
                          Department
                        </label>
                        <select
                          className="form-select"
                          value={form.department}
                          onChange={(e) =>
                            setForm({ ...form, department: e.target.value })
                          }
                        >
                          <option value="">Select Department</option>
                          {deptList.map((d) => (
                            <option key={d.deptid} value={d.deptname}>
                              {d.deptname}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Scheme */}
                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-medium">
                          Scheme Name
                        </label>
                        <select
                          className="form-select"
                          value={form.scheme_name}
                          onChange={(e) =>
                            setForm({ ...form, scheme_name: e.target.value })
                          }
                        >
                          <option value="">Select Scheme</option>
                          {schemeList.map((s) => (
                            <option key={s.prjcatid} value={s.prjcatname}>
                              {s.prjcatname}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Financial Year + Budget Type */}
                      <div className="col-6">
                        <label className="form-label small fw-medium">
                          Financial Year
                        </label>
                        <select
                          className="form-select"
                          value={form.financial_year}
                          onChange={(e) =>
                            setForm({ ...form, financial_year: e.target.value })
                          }
                        >
                          <option value="">Select Year</option>
                          {years.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label small fw-medium">
                          Budget Type
                        </label>
                        <select
                          className="form-select"
                          value={form.budget_type}
                          onChange={(e) =>
                            setForm({ ...form, budget_type: e.target.value })
                          }
                        >
                          <option value="Regular">Regular</option>
                          <option value="Additional">Additional</option>
                        </select>
                      </div>

                      {/* Status */}
                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-medium">
                          Status
                        </label>
                        <select
                          className="form-select"
                          value={form.status}
                          onChange={(e) =>
                            setForm({ ...form, status: e.target.value })
                          }
                        >
                          <option value="">Select Status</option>
                          {[
                            "Pending",
                            "Approved",
                            "Sanctioned",
                            "Disbursed",
                            "Completed",
                            "Rejected",
                          ].map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Amounts */}
                      <div className="col-12">
                        <div className="row g-2">
                          <div className="col-4">
                            <label className="form-label small fw-medium">
                              Subsidy (₹)
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="0.00"
                              step="0.01"
                              value={form.subsidy_amount}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  subsidy_amount: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-4">
                            <label className="form-label small fw-medium">
                              Beneficiary (₹)
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="0.00"
                              step="0.01"
                              value={form.beneficiary_amount}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  beneficiary_amount: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-4">
                            <label className="form-label small fw-medium">
                              Total (₹)
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="0.00"
                              step="0.01"
                              value={form.total_amount}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  total_amount: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0 px-4 pb-4">
                  <button
                    type="button"
                    className="btn btn-light px-4"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn text-white px-4"
                    style={{
                      background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                    }}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-2"></i>
                        {editItem ? "Update" : "Save"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Beneficiary;
